import { useState, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useCreateDataExtractionModel, useCreateDataExtractionQueue } from '@/hooks';
import { useDataExtractionReport } from '@/context/DataExtractionReport';

interface UseConfirmationModalHierarchyParams {
	onCancel?: () => void;
}

export const useConfirmationModalHierarchy = (params?: UseConfirmationModalHierarchyParams) => {
	const [saveAsTemplate, setSaveAsTemplate] = useState<boolean>(false);
	const [templateName, setTemplateName] = useState<string>('');
	const [showSuccessModal, setShowSuccessModal] = useState<boolean>(false);
	const [showErrorModal, setShowErrorModal] = useState<boolean>(false);
	const [isConfirmButtonDisabled, setIsConfirmButtonDisabled] = useState<boolean>(false);

	const history = useHistory();
	const { dataExtractionReport } = useDataExtractionReport();
	const { mutateAsync: createDataExtractionTemplate } = useCreateDataExtractionModel();
	const { mutateAsync: createDataExtractionQueue } = useCreateDataExtractionQueue();

	const handleSaveAsTemplateChange = useCallback((checked: boolean) => {
		setSaveAsTemplate(checked);
		if (!checked) {
			setTemplateName('');
		}
	}, []);

	const handleTemplateNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		setTemplateName(e.target.value);
	}, []);

	const handleConfirm = useCallback(async () => {
		if (!dataExtractionReport) return;
		setIsConfirmButtonDisabled(true);

		try {
			const queuePayload = {
				organization_id: dataExtractionReport.organization_id!,
				company_id: dataExtractionReport.company_id!,
				data_extraction_id: dataExtractionReport.id!
			};

			if (saveAsTemplate) {
				const templatePayload = {
					organization_id: dataExtractionReport.organization_id!,
					company_id: dataExtractionReport.company_id!,
					model_name: templateName!,
					data_extraction_id: dataExtractionReport.id!
				};

				await createDataExtractionTemplate(templatePayload);
			}

			await createDataExtractionQueue(queuePayload);
			setShowSuccessModal(true);
		} catch (error) {
			console.error('Error:', error);
			setShowErrorModal(true);
		}
	}, [saveAsTemplate, templateName, dataExtractionReport, createDataExtractionTemplate, createDataExtractionQueue]);

	const handleSuccessModalClose = useCallback(() => {
		setShowSuccessModal(false);
		params?.onCancel?.();
		setIsConfirmButtonDisabled(false);
	}, [params]);

	const handleErrorModalClose = useCallback(() => {
		setShowErrorModal(false);
		setIsConfirmButtonDisabled(false);
	}, []);

	const handleGoToHome = useCallback(() => {
		setShowSuccessModal(false);
		params?.onCancel?.();
		history.push('/settings/data-extraction');
	}, [history, params]);

	return {
		saveAsTemplate,
		templateName,
		showSuccessModal,
		showErrorModal,
		handleSaveAsTemplateChange,
		handleTemplateNameChange,
		handleConfirm,
		handleSuccessModalClose,
		handleErrorModalClose,
		handleGoToHome,
		isConfirmButtonDisabled
	};
};
